/**
 * Harvest.ts — API client module for CampMinder credential harvest endpoints.
 *
 * All functions are pure HTTP wrappers. No Redux or React imports.
 * Service thunks in src/services/HarvestService.ts call these functions.
 *
 * Endpoints spec: TDD §5.1
 */
import axios, { AxiosError, AxiosResponse } from 'axios'
import { config } from '@/config'
import { TCSAPIResponse } from '@/types/types'
import { formatResponse } from './helpers/formatResponse'
import type {
  HarvestStartResponse,
  HarvestStatusResponse,
  HarvestSelectClientIdResponse,
  HarvestTerminateResponse,
} from '@/types/DashboardV1.types'

export const HarvestAPI = {
  /**
   * POST /v1/credentials/harvest/start
   * Initiates the Browserbase harvest session. Returns sessionId + liveViewUrl.
   */
  start: async (
    token: string,
    input: { appCode: string }
  ): Promise<{ data: HarvestStartResponse | undefined; err: string | undefined }> => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/credentials/harvest/start`,
        headers: { Authorization: `Bearer ${token}` },
        data: input,
      })
      return formatResponse<HarvestStartResponse>(resp.data, null)
    } catch (error) {
      return formatResponse<HarvestStartResponse>(null, error as AxiosError)
    }
  },

  /**
   * GET /v1/credentials/harvest/:sessionId/status
   * Polls harvest session state. Primarily used for recovery; real-time
   * progress arrives via Socket.IO harvest:progress events.
   */
  getStatus: async (
    token: string,
    sessionId: string
  ): Promise<{ data: HarvestStatusResponse | undefined; err: string | undefined }> => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/credentials/harvest/${sessionId}/status`,
        headers: { Authorization: `Bearer ${token}` },
      })
      return formatResponse<HarvestStatusResponse>(resp.data, null)
    } catch (error) {
      return formatResponse<HarvestStatusResponse>(null, error as AxiosError)
    }
  },

  /**
   * POST /v1/credentials/harvest/:sessionId/select-client-id
   * Pre-harvest camp selection (v3 Plaid-style sequencing).
   * Response is { state: 'camp_selected', selectedClientIdPreharvest }.
   * NOT harvest completion — caller continues listening for harvest:progress events.
   */
  selectClientId: async (
    token: string,
    sessionId: string,
    clientId: string
  ): Promise<{
    data: HarvestSelectClientIdResponse | undefined
    err: string | undefined
  }> => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/credentials/harvest/${sessionId}/select-client-id`,
        headers: { Authorization: `Bearer ${token}` },
        data: { clientId },
      })
      return formatResponse<HarvestSelectClientIdResponse>(resp.data, null)
    } catch (error) {
      return formatResponse<HarvestSelectClientIdResponse>(null, error as AxiosError)
    }
  },

  /**
   * POST /v1/credentials/harvest/:sessionId/terminate
   * User-initiated cancellation. Backend sets failureReason based on prior state:
   * 'user_cancelled_camp_picker' if awaiting_camp_selection, else 'user_terminated'.
   */
  terminate: async (
    token: string,
    sessionId: string
  ): Promise<{ data: HarvestTerminateResponse | undefined; err: string | undefined }> => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/credentials/harvest/${sessionId}/terminate`,
        headers: { Authorization: `Bearer ${token}` },
      })
      return formatResponse<HarvestTerminateResponse>(resp.data, null)
    } catch (error) {
      return formatResponse<HarvestTerminateResponse>(null, error as AxiosError)
    }
  },
}
